<template>
  <div class="create-contract container container_30">
    <template v-if="!contractCreated">
      <form novalidate @submit.prevent="submit">
        <div class="create-contract__title">{{ i18n.cont_new_contract() }}</div>
        <div class="create-contract__fields-wrapper">
          <div class="create-contract__field">
            <input-field
              v-model="form.counterparty"
              name="contract-counterparty"
              v-validate="'required'"
              title="Counterparty"
              :label="i18n.cont_counterparty_email_or_id()"
            />
          </div>
          <div class="create-contract__field">
            <input-field
              v-model="form.contractNumber"
              name="contract-number"
              v-validate="'required'"
              title="Contract number"
              :label="i18n.cont_contract_number()"
            />
          </div>
        </div>
        <div class="create-contract__fields-wrapper">
          <div class="create-contract__field">
            <date-field-flatpickr
              v-model="form.startTime"
              name="contract-start-date"
              v-validate="'required'"
              :key="`create-contract-${form.endTime}`"
              title="Contract start date"
              :disableBefore="new Date().toString()"
              :disableAfter="form.endTime"
              :label="i18n.lbl_start_date()"
            />
          </div>
          <div class="create-contract__field">
            <date-field-flatpickr
              v-model="form.endTime"
              :key="`create-contract-${form.startTime}`"
              name="contract-end-date"
              v-validate="'required'"
              title="Contract end date"
              :disableBefore="disableBeforeDate()"
              :label="i18n.lbl_end_date()"
            />
          </div>
        </div>
        <div class="create-contract__fields-wrapper--secondary">
          <div class="create-contract__document-wrapper">
            <h3 class="field-title">{{i18n.lbl_document()}}</h3>
            <file-field fileType="image"
                        name="contract-file-field"
                        v-validate="'required'"
                        :type="documentTypes.contract"
                        v-model="documents.contract"/>
          </div>
          <div class="create-contract__note-wrapper">
            <h3 class="field-title">{{i18n.lbl_note()}}</h3>
            <textarea-field v-model="form.note"
                            name="contract-note-field"
                            v-validate="'required'"/>
          </div>
        </div>
        <div class="create-contract__submit-wrapper">
          <md-button class="md-primary btn"
                      type="submit"
                      :disabled="isPending">
            {{i18n.cont_sign_and_send()}}
          </md-button>
        </div>
      </form>
    </template>
    <template v-else>
      <contracts-details :id="requestId" :type="CONTRACTS_DETAILS_TYPES.requests" @close-details="contractCreated = false"/>
    </template>
  </div>
</template>

<script>
import FileField from '@com/fields/FileField'
import FormMixin from '@com/mixins/form.mixin'
import ContractsDetails from '../Contracts.Details'

import { i18n } from '@js/i18n'
import { documentTypes, CONTRACTS_DETAILS_TYPES } from '@const'

import { contractsService } from '@api/contracts.service'
import { accountsService } from '@api/accounts.service'
import { fileService } from '@api/file.service'
import { Keypair } from 'tokend-js-sdk'

import { DateHelper } from '@js/helpers/date.helper'
import { ErrorHandler } from '@js/errors/error_handler'
import { EventDispatcher } from '@js/events/event_dispatcher'

import { mapGetters, mapActions } from 'vuex'
import { vuexTypes } from '@vuex/types'
import {errors} from '@js/errors/factory'

export default {
  components: { FileField, ContractsDetails },
  mixins: [FormMixin],
  data: _ => ({
    form: {
      counterparty: '',
      contractNumber: '',
      startTime: null,
      endTime: null,
      note: ''
    },
    documents: {
      contract: null
    },
    requestId: null,
    contractCreated: false,
    i18n,
    documentTypes,
    CONTRACTS_DETAILS_TYPES
  }),

  created () {
  },

  computed: {
    ...mapGetters([
      vuexTypes.masterAccountId,
      vuexTypes.factorData
    ])
  },

  methods: {
    ...mapActions({
      loadList: vuexTypes.GET_USER_CONTRACT_CREATION_REQUESTS,
      updateTwoFactorData: vuexTypes.SET_TWO_FACTOR_STATE
    }),
    async submit () {
      if (!await this.isValid()) return
      this.disable()

      try {
        const counterparty = await this.loadCounterparty()
        if (!counterparty) {
          EventDispatcher.dispatchShowErrorEvent(i18n.cont_email_invalid())
          this.enable()
          return
        }

        const contract = this.documents.contract
        if (contract && !contract.isUploaded) {
          const contractKey = await fileService.uploadFile(contract.getDetailsForUpload())
          contract.setKey(contractKey)
        }
        const details = {
          contract: contract.getDetailsForSave(),
          contract_number: this.form.contractNumber,
          note: this.form.note
        }
        const request = await contractsService.createContractRequest({
          customer: counterparty,
          escrow: this.masterAccountId,
          startTime: DateHelper.toUnixTime(this.form.startTime),
          endTime: DateHelper.toUnixTime(this.form.endTime),
          details: details
        })
        this.requestId = request.contractRequestID()
        this.loadList()
        this.contractCreated = true
      } catch (error) {
        if (error instanceof errors.TFAExceededCodeError) {
          EventDispatcher.dispatchShowErrorEvent(i18n.tfa_to_many_attempts())
        } else {
          ErrorHandler.processUnexpected(error)
        }
      }

      this.enable()
    },
    async loadCounterparty () {
      const providedCounterparty = this.form.counterparty
      if (Keypair.isValidPublicKey(providedCounterparty)) {
        return providedCounterparty
      }
      const counterparty = await accountsService.loadAccountIdByEmail(providedCounterparty)
      return counterparty
    },
    disableBeforeDate () {
      console.log('dis before')
      let date = new Date()
      if (this.form.startTime !== '') {
        date = new Date(this.form.startTime)
      }
      return date.setDate(date.getDate())
    }
  }
}
</script>

<style lang="scss" scoped>
  @import "~scss/mixins";
  @import "~scss/variables";

  .create-contract {
    background: $default-white;
  }

  .create-contract__title {
    padding: 1.5rem;
    font-size: 1.5rem;
    line-height: 1.625rem;
  }

  .create-contract__fields-wrapper {
    display: flex;
    margin: auto 1.5rem;
    align-items: center;
    justify-content: space-around;
  }

  .create-contract__fields-wrapper--secondary {
    display: flex;
    margin: auto 1.5rem;
    justify-content: space-between;
  }

  /*.create-contract__field {
    width: 24%;
  }*/

  .create-contract__document-wrapper {
    width: 42%;
  }

  .create-contract__note-wrapper {
    width: 56%;
  }

  .field-title {
    margin-bottom: 1rem;
  }

  .create-contract__submit-wrapper {
    display: flex;
    justify-content: flex-end;
    margin-top: 1rem;
  }
</style>



// WEBPACK FOOTER //
// src/vue/app/contracts/index/new/Contracts.CreateNew.vue